import { z } from 'zod';

export { z };

// ---------- primitives ----------

export const SCHEMA_VERSION = 1 as const;

export const ID_PATTERN = /^[a-z]{2,8}_[a-z0-9]{12,40}$/;
export const Id = z.string().regex(ID_PATTERN, 'invalid id');
export type Id = z.infer<typeof Id>;

/** Unpredictable, URL-safe id. Works in browsers and Node 20+. */
export function newId(prefix: EntityPrefix): string {
  return `${prefix}_${crypto.randomUUID().replaceAll('-', '')}`;
}

export const Slug = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'lowercase letters, numbers and single dashes only');

export const IsoDate = z.iso.datetime({ offset: true });
export const Sha256 = z.string().regex(/^[a-f0-9]{64}$/);
export const ShortText = z.string().trim().min(1).max(200);
export const LongText = z.string().max(20_000);

export const DIMENSIONS = [
  'minecraft:overworld',
  'minecraft:the_nether',
  'minecraft:the_end',
] as const;
export const Dimension = z.enum(DIMENSIONS);
export type Dimension = z.infer<typeof Dimension>;
export const DIMENSION_LABELS: Record<Dimension, string> = {
  'minecraft:overworld': 'Overworld',
  'minecraft:the_nether': 'Nether',
  'minecraft:the_end': 'End',
};

const XZ_LIMIT = 30_000_000;
export const BlockX = z.number().int().min(-XZ_LIMIT).max(XZ_LIMIT);
export const BlockY = z.number().int().min(-2048).max(2048);

/** Canon is about the story; it is independent of whether something is published. */
export const CanonStatus = z.enum(['established', 'draft-idea', 'open-question']);
export type CanonStatus = z.infer<typeof CanonStatus>;

/** Editorial readiness of a draft. Only `ready` revisions may be selected for a release. */
export const EditorialStatus = z.enum(['draft', 'ready']);

// ---------- YouTube ----------

const YT_ID = /^[A-Za-z0-9_-]{11}$/;
export const YouTubeId = z.string().regex(YT_ID, 'not a YouTube video id');

/** Accepts a bare video id or a recognised YouTube URL. Returns null for anything else. */
export function parseYouTubeId(input: string): string | null {
  const s = input.trim();
  if (YT_ID.test(s)) return s;
  let url: URL;
  try {
    url = new URL(s);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  const host = url.hostname.replace(/^(www|m)\./, '');
  let id: string | null | undefined = null;
  if (host === 'youtu.be') id = url.pathname.split('/')[1];
  else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
    const [, first, second] = url.pathname.split('/');
    if (first === 'watch') id = url.searchParams.get('v');
    else if (first === 'embed' || first === 'shorts' || first === 'live') id = second;
  }
  return id && YT_ID.test(id) ? id : null;
}

// ---------- entities ----------

export const ENTITY_PREFIXES = {
  world: 'wld',
  character: 'chr',
  episode: 'ep',
  lore: 'lore',
  landmark: 'lmk',
  secret: 'sec',
  asset: 'ast',
  snapshot: 'snap',
  release: 'rel',
  job: 'job',
  preview: 'prv',
  kid: 'kid',
  skin: 'skn',
} as const;
export type EntityPrefix = (typeof ENTITY_PREFIXES)[keyof typeof ENTITY_PREFIXES];

const Common = z.object({
  schemaVersion: z.literal(SCHEMA_VERSION),
  id: Id,
  createdAt: IsoDate,
  updatedAt: IsoDate,
  revision: z.number().int().min(1),
});

/** Fields an editor supplies. Common metadata is managed by the server. */
export const WorldInput = z.object({
  name: ShortText,
  minecraftVersion: z.string().regex(/^\d+(\.\d+){1,2}$/),
  dimensions: z.array(Dimension).min(1),
  active: z.boolean(),
});

export const CharacterInput = z.object({
  slug: Slug,
  displayName: ShortText,
  tagline: z.string().max(200).default(''),
  biography: LongText,
  canonStatus: CanonStatus,
  /** Unresolved questions shown honestly on the character page. */
  unknowns: z.array(ShortText).max(20).default([]),
  /** null until the family supplies an approved skin; UI shows a labelled placeholder. */
  skinAssetId: Id.nullable().default(null),
  /** Illustration (concept art) shown on character cards and pages; separate from the skin. */
  artworkAssetId: Id.nullable().default(null),
  modelType: z.enum(['classic', 'slim']).default('classic'),
  accent: z.enum(['red', 'purple', 'green']).default('green'),
  editorialStatus: EditorialStatus.default('draft'),
});

export const Chapter = z.object({ t: z.number().int().min(0).max(86_400), label: ShortText });

export const EpisodeInput = z.object({
  slug: Slug,
  number: z.number().int().min(1).max(10_000),
  title: ShortText,
  youtubeId: YouTubeId.nullable().default(null),
  summary: LongText.default(''),
  thumbnailAssetId: Id.nullable().default(null),
  chapters: z.array(Chapter).max(100).default([]),
  landmarkIds: z.array(Id).max(200).default([]),
  /** The prepared map snapshot showing the world after this episode. */
  snapshotId: Id.nullable().default(null),
  editorialStatus: EditorialStatus.default('draft'),
});

export const LoreInput = z.object({
  slug: Slug,
  title: ShortText,
  body: LongText,
  canonStatus: CanonStatus,
  characterIds: z.array(Id).max(50).default([]),
  landmarkIds: z.array(Id).max(50).default([]),
  editorialStatus: EditorialStatus.default('draft'),
});

export const LANDMARK_ICONS = [
  'castle',
  'house',
  'cave',
  'portal',
  'farm',
  'tower',
  'mine',
  'other',
] as const;
export const LandmarkInput = z.object({
  slug: Slug,
  worldId: Id,
  dimension: Dimension,
  x: BlockX,
  y: BlockY,
  z: BlockX,
  name: ShortText,
  description: LongText.default(''),
  icon: z.enum(LANDMARK_ICONS).default('other'),
  /** A cave entrance marker does not promise an interior render. */
  underground: z.boolean().default(false),
  episodeLinks: z
    .array(
      z.object({ episodeId: Id, t: z.number().int().min(0).max(86_400).nullable().default(null) }),
    )
    .max(100)
    .default([]),
  screenshotAssetIds: z.array(Id).max(20).default([]),
  editorialStatus: EditorialStatus.default('draft'),
});

export const SecretInput = z.object({
  slug: Slug,
  title: ShortText,
  puzzle: z.object({
    kind: z.literal('find-symbols'),
    /** Symbol ids placed in the site (see ui `ka-spider-symbol`). */
    symbolIds: z.array(Slug).min(1).max(12),
    hint: ShortText,
  }),
  /** Must reference lore that is itself published in the same release. */
  rewardLoreId: Id,
  progressVersion: z.number().int().min(1).default(1),
  editorialStatus: EditorialStatus.default('draft'),
});

export const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
export const UploadRequest = z.object({
  kind: z.enum(['image', 'skin']),
  contentType: z.enum(IMAGE_TYPES),
  bytes: z
    .number()
    .int()
    .min(1)
    .max(5 * 1024 * 1024),
  width: z.number().int().min(1).max(4096),
  height: z.number().int().min(1).max(4096),
  sha256: Sha256,
  alt: z.string().max(300).default(''),
});
export const AssetInput = UploadRequest;

export const World = Common.extend(WorldInput.shape);
export const Character = Common.extend(CharacterInput.shape);
export const Episode = Common.extend(EpisodeInput.shape);
export const LoreEntry = Common.extend(LoreInput.shape);
export const Landmark = Common.extend(LandmarkInput.shape);
export const Secret = Common.extend(SecretInput.shape);
export const Asset = Common.extend({ ...UploadRequest.shape, key: z.string() });
export type World = z.infer<typeof World>;
export type Character = z.infer<typeof Character>;
export type Episode = z.infer<typeof Episode>;
export type LoreEntry = z.infer<typeof LoreEntry>;
export type Landmark = z.infer<typeof Landmark>;
export type Secret = z.infer<typeof Secret>;
export type Asset = z.infer<typeof Asset>;

/** Editable entity kinds exposed by `/api/admin/:entity`. */
export const EDITABLE = {
  worlds: { prefix: 'wld', input: WorldInput, record: World },
  characters: { prefix: 'chr', input: CharacterInput, record: Character },
  episodes: { prefix: 'ep', input: EpisodeInput, record: Episode },
  lore: { prefix: 'lore', input: LoreInput, record: LoreEntry },
  landmarks: { prefix: 'lmk', input: LandmarkInput, record: Landmark },
  secrets: { prefix: 'sec', input: SecretInput, record: Secret },
} as const;
export type EditableKind = keyof typeof EDITABLE;
export const EditableKind = z.enum(Object.keys(EDITABLE) as [EditableKind, ...EditableKind[]]);

// ---------- map snapshots ----------

/**
 * A logical asset path inside a render bundle, exactly as the viewer requests it.
 * Rejects traversal, absolute paths, backslashes, empty segments and odd characters.
 */
export const LogicalPath = z
  .string()
  .min(1)
  .max(512)
  .refine(isSafeLogicalPath, 'unsafe asset path');

export function isSafeLogicalPath(p: string): boolean {
  if (p.startsWith('/') || p.includes('\\') || p.includes('\0')) return false;
  if (!/^[A-Za-z0-9._\-/]+$/.test(p)) return false;
  return p.split('/').every((seg) => seg !== '' && seg !== '.' && seg !== '..');
}

export const MapAssetEntry = z.object({
  path: LogicalPath,
  sha256: Sha256,
  /** Size in bytes of the stored (possibly compressed) object. */
  size: z
    .number()
    .int()
    .min(0)
    .max(512 * 1024 * 1024),
  contentType: z.string().min(1).max(100),
  contentEncoding: z.enum(['gzip']).nullable().default(null),
  dimension: Dimension.nullable().default(null),
});
export type MapAssetEntry = z.infer<typeof MapAssetEntry>;

export const MapManifest = z
  .object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    renderer: z.object({ name: z.literal('bluemap'), version: z.string().min(1).max(40) }),
    configFingerprint: Sha256,
    dimensions: z.array(Dimension).min(1),
    /** Viewer entry point relative to the bundle root, e.g. "index.html". */
    entry: LogicalPath,
    assets: z.array(MapAssetEntry).min(1).max(2_000_000),
  })
  .superRefine((m, ctx) => {
    const seen = new Set<string>();
    for (const a of m.assets) {
      if (seen.has(a.path)) ctx.addIssue({ code: 'custom', message: `duplicate path ${a.path}` });
      seen.add(a.path);
    }
    if (!seen.has(m.entry)) ctx.addIssue({ code: 'custom', message: 'entry not in assets' });
  });
export type MapManifest = z.infer<typeof MapManifest>;

export const SnapshotStatus = z.enum(['allocated', 'validating', 'ready', 'failed']);
export const MapSnapshot = Common.extend({
  worldId: Id,
  capturedAt: IsoDate,
  sourceFingerprint: Sha256,
  renderer: z.object({ name: z.literal('bluemap'), version: z.string() }),
  configFingerprint: Sha256,
  dimensions: z.array(Dimension),
  manifestKey: z.string().nullable(),
  counts: z.object({ files: z.number().int(), bytes: z.number().int() }),
  warnings: z.array(z.string().max(500)).max(200),
  status: SnapshotStatus,
  error: z.string().nullable(),
});
export type MapSnapshot = z.infer<typeof MapSnapshot>;

export const AllocateSnapshotRequest = z.object({
  worldId: Id,
  capturedAt: IsoDate,
  sourceFingerprint: Sha256,
  renderer: z.object({ name: z.literal('bluemap'), version: z.string().min(1).max(40) }),
  configFingerprint: Sha256,
  dimensions: z.array(Dimension).min(1),
  warnings: z.array(z.string().max(500)).max(200).default([]),
  /** Lets the publisher retry allocation safely. */
  idempotencyKey: z.string().regex(/^[A-Za-z0-9_-]{8,80}$/),
});

export const HeartbeatRequest = z.object({
  agentVersion: z.string().max(40),
  state: z.enum(['idle', 'waitingForClose', 'working', 'paused', 'failed']),
  message: z.string().max(300).default(''),
  activeJob: z.string().max(80).nullable().default(null),
});

// ---------- releases ----------

export const ReleaseStatus = z.enum(['preparing', 'complete', 'failed']);
export const Release = Common.extend({
  label: ShortText,
  contentManifestKey: z.string().nullable(),
  snapshotIds: z.array(Id),
  parentReleaseId: Id.nullable(),
  status: ReleaseStatus,
  preparedBy: z.string().max(200),
  activatedAt: IsoDate.nullable(),
});
export type Release = z.infer<typeof Release>;

export const Selection = z.object({
  kind: EditableKind,
  id: Id,
  revision: z.number().int().min(1),
});
export type Selection = z.infer<typeof Selection>;

export const CreateReleaseRequest = z.object({
  label: ShortText,
  selections: z.array(Selection).min(1).max(2000),
  expectedSiteRevision: z.number().int().min(0),
  idempotencyKey: z.string().regex(/^[A-Za-z0-9_-]{8,80}$/),
});

export const JobStage = z.enum([
  'queued',
  'preparing',
  'copying',
  'validating',
  'activating',
  'done',
  'failed',
]);
export const PublicationJob = Common.extend({
  idempotencyKey: z.string(),
  releaseId: Id,
  expectedSiteRevision: z.number().int(),
  stage: JobStage,
  error: z.string().nullable(),
  counts: z.object({ objectsCopied: z.number().int(), objectsTotal: z.number().int() }),
});
export type PublicationJob = z.infer<typeof PublicationJob>;

export const SiteState = z.object({
  currentReleaseId: Id.nullable(),
  revision: z.number().int().min(0),
  publishingPaused: z.boolean(),
});
export type SiteState = z.infer<typeof SiteState>;

/** Response of GET /api/public/site. */
export const PublicSite = z.object({
  releaseId: Id.nullable(),
  manifestUrl: z.string().nullable(),
  siteRevision: z.number().int(),
});
export type PublicSite = z.infer<typeof PublicSite>;

// ---------- public release manifest (immutable, what the site renders) ----------

const PublicCommon = z.object({ id: Id, revision: z.number().int() });

export const PublicMap = z.object({
  snapshotId: Id,
  /** Absolute path under /releases/ (or /preview/ for drafts) ending in '/'. */
  baseUrl: z.string().regex(/^\/(releases|preview)\/[A-Za-z0-9_\-/]+\/$/),
  entry: LogicalPath,
  worldId: Id,
  worldName: ShortText,
  capturedAt: IsoDate,
  dimensions: z.array(Dimension).min(1),
});
export type PublicMap = z.infer<typeof PublicMap>;

export const ReleaseManifest = z.object({
  schemaVersion: z.literal(SCHEMA_VERSION),
  releaseId: Id,
  label: ShortText,
  createdAt: IsoDate,
  publishedAt: IsoDate.nullable(),
  /** True for authenticated draft previews and local fixtures. */
  draft: z.boolean(),
  fixture: z.boolean().default(false),
  episodes: z.array(
    PublicCommon.extend(
      EpisodeInput.omit({ editorialStatus: true, snapshotId: true }).shape,
    ).extend({ map: PublicMap.nullable() }),
  ),
  characters: z.array(PublicCommon.extend(CharacterInput.omit({ editorialStatus: true }).shape)),
  lore: z.array(PublicCommon.extend(LoreInput.omit({ editorialStatus: true }).shape)),
  landmarks: z.array(PublicCommon.extend(LandmarkInput.omit({ editorialStatus: true }).shape)),
  secrets: z.array(PublicCommon.extend(SecretInput.omit({ editorialStatus: true }).shape)),
  /** Asset id -> URL under the release prefix. */
  assets: z.record(
    Id,
    z.object({
      url: z.string(),
      width: z.number().int(),
      height: z.number().int(),
      alt: z.string(),
    }),
  ),
});
export type ReleaseManifest = z.infer<typeof ReleaseManifest>;
export type PublicEpisode = ReleaseManifest['episodes'][number];
export type PublicCharacter = ReleaseManifest['characters'][number];
export type PublicLore = ReleaseManifest['lore'][number];
export type PublicLandmark = ReleaseManifest['landmarks'][number];
export type PublicSecret = ReleaseManifest['secrets'][number];

/** Every id a manifest references must resolve inside the same manifest. */
export function findDanglingReferences(m: ReleaseManifest): string[] {
  const ids = new Set([
    ...m.episodes.map((e) => e.id),
    ...m.characters.map((c) => c.id),
    ...m.lore.map((l) => l.id),
    ...m.landmarks.map((l) => l.id),
  ]);
  const problems: string[] = [];
  const need = (
    from: string,
    id: string | null | undefined,
    set: Set<string> | Record<string, unknown> = ids,
  ) => {
    if (!id) return;
    const ok = set instanceof Set ? set.has(id) : id in set;
    if (!ok) problems.push(`${from} -> ${id}`);
  };
  for (const e of m.episodes) {
    e.landmarkIds.forEach((id) => need(e.id, id));
    need(e.id, e.thumbnailAssetId, m.assets);
  }
  for (const l of m.lore) [...l.characterIds, ...l.landmarkIds].forEach((id) => need(l.id, id));
  for (const l of m.landmarks) {
    l.episodeLinks.forEach((x) => need(l.id, x.episodeId));
    l.screenshotAssetIds.forEach((id) => need(l.id, id, m.assets));
  }
  for (const c of m.characters) {
    need(c.id, c.skinAssetId, m.assets);
    need(c.id, c.artworkAssetId, m.assets);
  }
  for (const s of m.secrets) need(s.id, s.rewardLoreId);
  return problems;
}

// ---------- map deep links ----------

export const MapLink = z.object({
  release: Id.optional(),
  snapshot: Id.optional(),
  dim: Dimension.optional(),
  landmark: Id.optional(),
  x: z.coerce.number().pipe(BlockX).optional(),
  y: z.coerce.number().pipe(BlockY).optional(),
  z: z.coerce.number().pipe(BlockX).optional(),
});
export type MapLink = z.infer<typeof MapLink>;

/** Parses map query params, silently dropping anything invalid (links get pasted around). */
export function parseMapLink(params: URLSearchParams): MapLink {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(MapLink.shape) as (keyof MapLink)[]) {
    const raw = params.get(key);
    if (raw === null) continue;
    const r = MapLink.shape[key].safeParse(raw);
    if (r.success && r.data !== undefined) out[key] = r.data;
  }
  return out as MapLink;
}

export function formatMapLink(link: MapLink): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(link)) if (v !== undefined) p.set(k, String(v));
  return p.toString();
}

// ---------- friends and families ----------

/**
 * Kid nicknames are picked from these words (adjective + creature + number), never typed freely:
 * free-text nicknames need owner approval, which arrives with the moderation queue.
 */
export const NICKNAME_WORDS = {
  first: [
    'Brave',
    'Clever',
    'Swift',
    'Sneaky',
    'Mighty',
    'Lucky',
    'Shiny',
    'Quiet',
    'Jolly',
    'Bold',
    'Fuzzy',
    'Golden',
  ],
  second: [
    'Spider',
    'Creeper',
    'Fox',
    'Axolotl',
    'Bee',
    'Golem',
    'Parrot',
    'Turtle',
    'Wolf',
    'Otter',
    'Panda',
    'Frog',
  ],
} as const;
const NICKNAME_RE = new RegExp(
  `^(${NICKNAME_WORDS.first.join('|')})(${NICKNAME_WORDS.second.join('|')})([1-9][0-9]?)$`,
);
export const Nickname = z.string().regex(NICKNAME_RE, 'pick a nickname from the list');

export function randomNickname(): string {
  const pick = <T>(a: readonly T[]) =>
    a[crypto.getRandomValues(new Uint32Array(1))[0]! % a.length]!;
  return `${pick(NICKNAME_WORDS.first)}${pick(NICKNAME_WORDS.second)}${1 + (crypto.getRandomValues(new Uint32Array(1))[0]! % 99)}`;
}

/** Spider-hunt progress; same shape as the signed-out localStorage copy. */
export const KidProgress = z.object({
  version: z.number().int().min(1).max(1000),
  found: z.array(Slug).max(100),
});
export type KidProgress = z.infer<typeof KidProgress>;

/** Pairing merge: union when both copies are for the same hunt version, else the newer version. */
export function mergeProgress(a: KidProgress | null, b: KidProgress | null): KidProgress | null {
  if (!a || !b) return a ?? b;
  if (a.version !== b.version) return a.version > b.version ? a : b;
  return { version: a.version, found: [...new Set([...a.found, ...b.found])] };
}

/** Pairing codes: 8 characters without look-alikes (no 0/O, 1/I/L). */
export const PAIRING_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
export const PairingCode = z
  .string()
  .transform((s) => s.toUpperCase().replace(/[\s-]/g, ''))
  .pipe(z.string().regex(new RegExp(`^[${PAIRING_ALPHABET}]{8}$`), 'invalid code'));

/** Single-use family sign-up codes, one per printed card (QR): same alphabet and length. */
export const InviteCode = PairingCode;

export interface Invite {
  code: string;
  /** Batch name, e.g. "School cards, Oct 2026". */
  label: string;
  createdAt: string;
  revoked: boolean;
  /** Set by the Cognito pre-sign-up trigger when a parent signs up with this code. */
  usedAt?: string;
  /** SHA-256 of the parent's lower-case email, so the same parent can retry; never the email. */
  usedBy?: string;
}

export interface KidProfile {
  id: string;
  nickname: string;
  createdAt: string;
  progress: KidProgress | null;
}

// ---------- API errors ----------

export const ERROR_CODES = [
  'bad_request',
  'unauthorized',
  'forbidden',
  'not_found',
  'conflict',
  'payload_too_large',
  'publishing_paused',
  'internal',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];
export interface ApiError {
  error: { code: ErrorCode; message: string; requestId: string; details?: unknown };
}

export interface Page<T> {
  items: T[];
  next: string | null;
}
